import { useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import usePermissions from "../../hooks/usePermissions";
import { FEATURES } from "../../hooks/usePermissions/permissions";
import {
  CreateSharingInvitationDocument,
  SharingInvitationsDocument,
  SharingInvitationType,
} from "../../__generated__/graphql";
import FormInput from "../FormInput";
import Button from "../Button";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { motion } from "motion/react";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import { useState, useEffect } from "react";

const sharingInvitationSchema = z.object({
  recipientEmails: z
    .string()
    .min(1, "Veuillez entrer au moins un email")
    .superRefine((value, ctx) => {
      // Only run validations if there's a value
      if (!value) return;

      // Check if separator is valid
      const hasValidSeparator = /^[^,;\s]*([,;\s]+[^,;\s]*)*$/.test(value);
      if (!hasValidSeparator) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Utilisez uniquement ',' ou ';' pour séparer les emails",
        });
        return;
      }

      // Validate email formats
      const emails = value.split(/[,;\s]+/).map((e) => e.trim());
      const invalidEmails = emails.filter(
        (email) => !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
      );
      if (invalidEmails.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Format d'email invalide",
        });
      }
    }),
});

type SharingInvitationFormData = z.infer<typeof sharingInvitationSchema>;

interface CreateSharingInvitationProps {
  isModalOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  invitationType?: SharingInvitationType;
}

export default function CreateSharingInvitationModal({
  isModalOpen,
  onClose,
  title,
  description,
  invitationType = SharingInvitationType.ExternalMember,
}: CreateSharingInvitationProps) {
  const { currentOrganization } = useUser();
  const { can } = usePermissions();

  const [isLoading, setIsLoading] = useState(false);

  const [createSharingInvitation] = useMutation(
    CreateSharingInvitationDocument
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    clearErrors,
  } = useForm<SharingInvitationFormData>({
    resolver: zodResolver(sharingInvitationSchema),
    mode: "onSubmit",
    defaultValues: {
      recipientEmails: "",
    },
  });

  useEffect(() => {
    if (isModalOpen) {
      reset({
        recipientEmails: "",
      });
    }
  }, [isModalOpen, reset]);

  if (!can(FEATURES.SHARING_INVITATION)) {
    return null;
  }

  if (!currentOrganization) {
    return null;
  }

  const handleClose = () => {
    reset();
    clearErrors();
    onClose();
  };

  const onSubmit = async (formData: SharingInvitationFormData) => {
    try {
      setIsLoading(true);
      const validatedEmails = formData.recipientEmails
        .split(/[,;\s]+/)
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);

      // TODO: maybe we'll need to handle this on the backend side and limit for a certain amount of emails
      await Promise.all(
        validatedEmails.map((email: string) =>
          createSharingInvitation({
            variables: {
              createSharingInvitationInput: {
                recipientEmail: email,
                invitationType,
              },
            },
            refetchQueries: [SharingInvitationsDocument],
          })
        )
      );

      toast.success("Invitation envoyée avec succès");
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de l'envoi de l'invitation"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const invitationTitleWording =
    invitationType === SharingInvitationType.OrganizationMember
      ? "Ajoutez un ou plusieurs membres à votre organisation"
      : "Invitez un ou plusieurs collaborateurs";

  const invitationDescriptionWording =
    invitationType === SharingInvitationType.OrganizationMember
      ? "Invitez des utilisateurs à rejoindre votre organisation pour partager des ressources et collaborer ensemble."
      : "Invitez des utilisateurs à établir une connexion avec vous pour partager des ressources et collaborer ensemble. Cette connexion permettra d'échanger des documents, de participer à des événements et activités communes.";

  return (
    <Dialog open={isModalOpen} onClose={handleClose} className="relative z-50" data-testid="invitation-modal">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 flex w-screen items-center justify-center p-4"
      >
        <DialogPanel className="max-w-lg space-y-4 bg-dark border border-input p-8 rounded-lg text-primary">
          <DialogTitle className="font-bold text-xl">
            {title ?? invitationTitleWording}
          </DialogTitle>
          <p className="text-sm text-secondary">
            {description ?? invitationDescriptionWording}
          </p>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormInput
              id="recipientEmails"
              label="Emails (séparés par une virgule)"
              type="text"
              register={register}
              error={errors.recipientEmails}
              placeholder="email@example.com, email2@example.com"
              dataTestId="invitation-emails-input"
            />
            <Button type="submit" isLoading={isLoading} dataTestId="invitation-submit-button">
              Inviter
            </Button>
          </form>
        </DialogPanel>
      </motion.div>
    </Dialog>
  );
}
